import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Textarea } from "@/components/ui/textarea"
import { GlassCard } from "./GlassCard"
import { AnimatedCard } from "./AnimatedCard"
import { CountdownTimer } from "./CountdownTimer"
import { useApexState } from "@/hooks/useApexState"
import { 
  Trophy, 
  Camera, 
  Upload,
  Heart,
  MessageCircle,
  Share2,
  Crown,
  X,
  CheckCircle,
  Star
} from "lucide-react"

interface Challenge {
  id: string
  title: string
  description: string
  prompt: string
  image: string
  deadline: Date
  reward: number
  participants: number
  isActive: boolean
  requirements: string[]
  hashtags: string[]
}

interface Submission {
  id: string
  userId: string
  user: {
    name: string
    tier: 'initiate' | 'creator' | 'architect'
    avatar: string
  }
  image: string
  caption: string
  likes: number
  comments: number
  isLiked: boolean
  isFeatured: boolean
  timestamp: string
}

interface StyleChallengeProps {
  challenge: Challenge
  onClose?: () => void
}

const mockChallenge: Challenge = {
  id: "motion-form-challenge",
  title: "Motion & Form",
  description: "Capture the intersection of movement and architecture",
  prompt: "Style your Kinetic Joggers in an architectural setting that represents motion and form",
  image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop",
  deadline: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 days from now
  reward: 500,
  participants: 127,
  isActive: true,
  requirements: [
    "Must feature Gameshape Kinetic Joggers",
    "Architectural or urban setting required",
    "Original photography only",
    "Include challenge hashtags"
  ],
  hashtags: ["#MotionAndForm", "#WearingGameshape", "#ShapeMyWorld"]
}

const mockSubmissions: Submission[] = [
  {
    id: "1",
    userId: "user1",
    user: {
      name: "Maya Rodriguez",
      tier: "architect",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop"
    },
    image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=300&h=400&fit=crop",
    caption: "Movement meets brutalist architecture. The clean lines mirror the precision of motion. #MotionAndForm #WearingGameshape",
    likes: 89,
    comments: 12,
    isLiked: false,
    isFeatured: true,
    timestamp: "2 hours ago"
  },
  {
    id: "2",
    userId: "user2", 
    user: {
      name: "Jordan Kim",
      tier: "creator",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop"
    },
    image: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=300&h=400&fit=crop",
    caption: "Urban exploration in motion. Every step tells a story. #ShapeMyWorld #MotionAndForm",
    likes: 56,
    comments: 8,
    isLiked: true,
    isFeatured: false,
    timestamp: "5 hours ago"
  }
]

export function StyleChallenge({ challenge = mockChallenge, onClose }: StyleChallengeProps) {
  const { state } = useApexState()
  const [showSubmissionForm, setShowSubmissionForm] = useState(false)
  const [submissions, setSubmissions] = useState<Submission[]>(mockSubmissions)
  const [newSubmission, setNewSubmission] = useState({
    image: "",
    caption: ""
  })
  const [likedSubmissions, setLikedSubmissions] = useState<string[]>(["2"])

  const toggleLike = (submissionId: string) => {
    setLikedSubmissions(prev => 
      prev.includes(submissionId) 
        ? prev.filter(id => id !== submissionId)
        : [...prev, submissionId]
    )
    
    setSubmissions(prev => 
      prev.map(sub => 
        sub.id === submissionId 
          ? { 
              ...sub, 
              likes: likedSubmissions.includes(submissionId) ? sub.likes - 1 : sub.likes + 1,
              isLiked: !likedSubmissions.includes(submissionId)
            }
          : sub
      )
    )
  }

  const getTierBadgeStyle = (tier: string) => {
    switch (tier) {
      case 'architect':
        return 'tier-badge-architect text-xs px-2 py-0.5'
      case 'creator':
        return 'tier-badge-creator text-xs px-2 py-0.5'
      default:
        return 'tier-badge-initiate text-xs px-2 py-0.5'
    }
  }

  const handleSubmit = () => {
    // Implementation would handle image upload and submission
    console.log('Submitting challenge entry:', newSubmission)
    setShowSubmissionForm(false)
    setNewSubmission({ image: "", caption: "" })
  }

  return (
    <div className="space-y-6">
      {/* Challenge Header */}
      <AnimatedCard hoverScale>
        <Card className="bg-card-gradient border-0 overflow-hidden">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-accent text-accent-foreground">
                    <Trophy size={12} className="mr-1" />
                    Active Challenge
                  </Badge>
                  {onClose && (
                    <Button variant="ghost" size="sm" onClick={onClose} className="ml-auto">
                      <X size={16} />
                    </Button>
                  )}
                </div>
                <h2 className="text-xl font-bold text-foreground mb-1">
                  {challenge.title}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {challenge.description}
                </p>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Challenge Image */}
            <div className="aspect-[16/9] rounded-lg overflow-hidden">
              <img
                src={challenge.image}
                alt={challenge.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Challenge Details */}
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-foreground mb-2">Challenge Prompt</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {challenge.prompt}
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">Requirements</h3>
                <ul className="space-y-1">
                  {challenge.requirements.map((req, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle size={12} className="text-accent flex-shrink-0" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-wrap gap-2">
                {challenge.hashtags.map(tag => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>

              {/* Stats and Countdown */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <CountdownTimer 
                    targetDate={challenge.deadline}
                    size="sm"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Participants</span>
                    <span className="font-medium">{challenge.participants}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Reward</span>
                    <span className="font-medium text-accent">+{challenge.reward} pts</span>
                  </div>
                </div>
              </div>

              {/* Join Challenge Button */}
              {!showSubmissionForm && (
                <Button 
                  onClick={() => setShowSubmissionForm(true)}
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
                >
                  <Camera size={16} className="mr-2" />
                  Join Challenge
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </AnimatedCard>

      {/* Submission Form */}
      {showSubmissionForm && (
        <AnimatedCard>
          <Card className="bg-card-gradient border-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Submit Your Entry</h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowSubmissionForm(false)}
                >
                  <X size={16} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Photo Upload */}
              <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center">
                <Upload size={32} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground mb-2">Upload your challenge photo</p>
                <Button variant="outline" size="sm">
                  Choose File
                </Button>
              </div>

              {/* Caption */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Caption
                </label>
                <Textarea
                  placeholder="Tell the story behind your photo..."
                  value={newSubmission.caption}
                  onChange={(e) => setNewSubmission(prev => ({ ...prev, caption: e.target.value }))}
                  className="bg-input/50 border-white/20"
                />
              </div>

              {/* Hashtags Reminder */}
              <div className="p-3 rounded-lg bg-accent/10 border border-accent/20">
                <p className="text-sm text-accent font-medium mb-1">Don't forget to include:</p>
                <div className="flex flex-wrap gap-1">
                  {challenge.hashtags.map(tag => (
                    <Badge key={tag} className="bg-accent/20 text-accent text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowSubmissionForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit}
                  className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground"
                >
                  Submit Entry
                </Button>
              </div>
            </CardContent>
          </Card>
        </AnimatedCard>
      )}

      {/* Recent Submissions */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Recent Submissions</h3>
          <Button variant="ghost" size="sm" className="text-primary">
            View All
          </Button>
        </div>

        <div className="space-y-4">
          {submissions.map(submission => (
            <AnimatedCard key={submission.id}>
              <Card className="bg-card-gradient border-0">
                <CardContent className="p-0">
                  {/* Submission Header */}
                  <div className="p-4 pb-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={submission.user.avatar} alt={submission.user.name} />
                          <AvatarFallback>{submission.user.name.slice(0, 2)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-foreground">{submission.user.name}</h4>
                            <Badge className={getTierBadgeStyle(submission.user.tier)}>
                              {submission.user.tier}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{submission.timestamp}</p>
                        </div>
                      </div>
                      {submission.isFeatured && (
                        <Badge className="bg-primary text-black text-xs">
                          <Star size={10} className="mr-1" />
                          Featured
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Submission Image */}
                  <div className="aspect-square overflow-hidden mx-4 rounded-lg">
                    <img
                      src={submission.image}
                      alt="Challenge submission"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Submission Content */}
                  <div className="p-4">
                    <p className="text-foreground leading-relaxed mb-3">
                      {submission.caption}
                    </p>

                    {/* Engagement */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => toggleLike(submission.id)}
                          className="flex items-center gap-2 hover:text-red-400 transition-colors"
                        >
                          <Heart 
                            size={18} 
                            className={likedSubmissions.includes(submission.id) ? "fill-red-400 text-red-400" : ""}
                          />
                          <span className="text-sm">{submission.likes}</span>
                        </button>
                        
                        <button className="flex items-center gap-2 hover:text-primary transition-colors">
                          <MessageCircle size={18} />
                          <span className="text-sm">{submission.comments}</span>
                        </button>
                        
                        <button className="flex items-center gap-2 hover:text-primary transition-colors">
                          <Share2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </AnimatedCard>
          ))}
        </div>
      </div>
    </div>
  )
}